import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { categoryOf, conditionLabel, accDetails, formatPrice } from "@/lib/store";
import { comboDiscount, discountText, suggestionsFor } from "@/lib/combos";
import { fetchProducts } from "@/lib/products";

const KEY = "rc_cart";
const CartContext = createContext(null);

const load = () => {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || "[]");
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
};

export const isAccessory = (p) => categoryOf(p) === "acessorios";
export const itemKey = (p, forModel = "") => `${p?.id}|${forModel}`;

const snapshot = (p) => ({
  id: p.id, model: p.model, category: p.category, condition: p.condition, storage: p.storage, color: p.color,
  price: Number(p.price) || 0, images: (p.images || []).slice(0, 1), acc_type: p.acc_type, wattage: p.wattage,
});

export const itemLine = (it, i) => {
  const p = it.product;
  const details = (isAccessory(p) ? accDetails(p) : [p.storage, p.color, conditionLabel(p)]).filter(Boolean).join(", ");
  const qty = it.qty > 1 ? `${it.qty}x ` : "";
  const forTxt = it.forModel ? ` para ${it.forModel}` : "";
  const price = p.price > 0 ? formatPrice(p.price * it.qty) : "sob consulta";
  return `${i + 1}. ${qty}*${p.model}*${forTxt}${details ? ` (${details})` : ""} — ${price}`;
};

export const cartTotal = (items) => items.reduce((s, it) => s + it.product.price * it.qty, 0);
export const cartDiscount = (items) => comboDiscount(items);

export const cartMessage = (items, shipping = null) => {
  const subtotal = cartTotal(items);
  const discount = cartDiscount(items);
  const hasQuote = items.some((it) => !(it.product.price > 0));
  const freight = shipping?.amount || 0;
  return [
    "Olá, RC iPhones! Montei este pedido pelo site:",
    "",
    ...items.map(itemLine),
    "",
    ...(discount.total > 0 || shipping ? [`Subtotal: ${formatPrice(subtotal)}`, ...discountText(discount)] : []),
    ...(shipping ? [`${shipping.label}: ${freight ? formatPrice(freight) : "R$ 0,00"}`] : []),
    `*Total${freight ? " com frete" : ""}: ${formatPrice(subtotal - discount.total + freight)}*${hasQuote ? " + itens sob consulta" : ""}`,
    "Podem confirmar a disponibilidade?",
  ].join("\n");
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(load);
  const [open, setOpen] = useState(false);
  const [upsellFor, setUpsellFor] = useState(null);
  const [interestFor, setInterestFor] = useState(null);
  const [catalog, setCatalog] = useState(null);

  useEffect(() => {
    fetchProducts().then(setCatalog).catch(() => setCatalog([]));
  }, []);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const has = useCallback((p, forModel = "") => items.some((it) => it.key === itemKey(p, forModel)), [items]);

  const add = useCallback((p, forModel = "", { silent = false } = {}) => {
    const key = itemKey(p, forModel);
    setItems((prev) => {
      const found = prev.find((it) => it.key === key);
      if (found) return isAccessory(p) ? prev.map((it) => (it.key === key ? { ...it, qty: it.qty + 1 } : it)) : prev;
      return [...prev, { key, forModel, qty: 1, product: snapshot(p) }];
    });
    if (categoryOf(p) === "iphone") setUpsellFor(snapshot(p));
    else if (!silent) toast.success(`${p.model} adicionado ao carrinho`, { action: { label: "Ver carrinho", onClick: () => setOpen(true) } });
  }, []);

  const setQty = useCallback((key, qty) => {
    setItems((prev) => (qty < 1 ? prev.filter((it) => it.key !== key) : prev.map((it) => (it.key === key ? { ...it, qty } : it))));
  }, []);

  const remove = useCallback((key) => setItems((prev) => prev.filter((it) => it.key !== key)), []);
  const clear = useCallback(() => setItems([]), []);

  const value = useMemo(
    () => ({ items, count: items.reduce((s, it) => s + it.qty, 0), open, setOpen, upsellFor, setUpsellFor, interestFor, setInterestFor, catalog, add, has, setQty, remove, clear }),
    [items, open, upsellFor, interestFor, catalog, add, has, setQty, remove, clear],
  );
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);

export const useInterestClick = (p, { offer = false } = {}) => {
  const { catalog, setInterestFor } = useCart();
  return (e) => {
    if (categoryOf(p) !== "iphone" || !catalog || !suggestionsFor(catalog, p.model).length) return false;
    e.preventDefault();
    setInterestFor(offer ? { ...p, _offer: true } : p);
    return true;
  };
};
